<template>
    <div class="realtime-logs">
        <div class="logs-header">
            <h3>实时日志</h3>
            <div class="logs-controls">
                <el-select v-model="logFilter" placeholder="筛选日志类型" size="small" style="width: 120px">
                    <el-option label="全部" value="" />
                    <el-option label="测量" value="measure" />
                    <el-option label="查询" value="query" />
                    <el-option label="配置" value="config" />
                </el-select>
                <el-button size="small" :loading="loadingLogs" @click="fetchLogsFromApi">刷新日志</el-button>
                <el-button size="small" @click="clearLogs">清空日志</el-button>
                <el-button size="small" @click="exportLogs">导出日志</el-button>
            </div>
        </div>

        <div class="logs-content">
            <el-scrollbar ref="logScrollbar" height="500px">
                <div class="log-container">
                    <div
                        v-for="(log, index) in filteredLogs"
                        :key="index"
                        class="log-entry"
                        :class="getLogClass(log)">
                        <div class="log-time">{{ formatTime(log.timestamp) }}</div>
                        <div class="log-type">{{ log.type.toUpperCase() }}</div>
                        <div class="log-message">
                            <div class="log-summary">{{ log.summary }}</div>
                            <div v-if="log.details" class="log-details">
                                <el-collapse>
                                    <el-collapse-item title="详细信息">
                                        <pre>{{ JSON.stringify(log.details, null, 2) }}</pre>
                                    </el-collapse-item>
                                </el-collapse>
                            </div>
                        </div>
                    </div>

                    <div v-if="filteredLogs.length === 0" class="no-logs">
                        <el-empty description="暂无日志数据" />
                    </div>
                </div>
            </el-scrollbar>
        </div>

        <div class="logs-footer">
            <div class="log-stats">
                <span>总计: {{ logs.length }} 条</span>
                <span v-if="logFilter">已筛选: {{ filteredLogs.length }} 条</span>
            </div>
            <div class="auto-scroll">
                <el-checkbox v-model="autoScroll">自动滚动</el-checkbox>
            </div>
        </div>
    </div>
</template>

<script>
import {ref, computed, nextTick, watch, onMounted, onUnmounted} from 'vue';
import axios from 'axios';
import {notify} from '../../assets/js/utils.js';
import {formatToBJ, nowUtcISOString} from '../../assets/js/time.js';

export default {
    name: 'RealtimeLogs',
    props: {
        selectedBox: {
            type: Object,
            default: null,
        },
    },
    setup(props) {
        const logs = ref([]);
        const logFilter = ref('');
        const autoScroll = ref(true);
        const logScrollbar = ref(null);
        const loadingLogs = ref(false);

        const pollingTimer = ref(null);
        const POLL_INTERVAL = 3000;
        let lastFetchTime = null;

        const filteredLogs = computed(() => {
            if (!logFilter.value) {
                return logs.value;
            }
            return logs.value.filter(log => log.type === logFilter.value);
        });

        // 添加日志
        const addLog = (logEntry) => {
            logs.value.unshift({
                ...logEntry,
                timestamp: nowUtcISOString(),
            });

            // 限制日志数量，避免内存占用过多
            if (logs.value.length > 1000) {
                logs.value = logs.value.slice(0, 1000);
            }

            // 自动滚动到底部
            if (autoScroll.value) {
                nextTick(() => {
                    if (logScrollbar.value) {
                        logScrollbar.value.setScrollTop(0);
                    }
                });
            }
        };

        // 添加命令发送日志
        const addCommandLog = (commandData) => {
            const typeMap = {
                'measure': '测量命令',
                'query': '查询命令',
                'config': '配置命令',
            };

            addLog({
                type: commandData.type,
                summary: `发送${typeMap[commandData.type] || commandData.type}到盒子 `
                        + `${props.selectedBox?.name || commandData.data.box_id}`,
                details: commandData.data,
                level: 'info',
            });
        };

        // 添加MQTT消息日志
        const addMqttMessageLog = (message) => {
            addLog({
                type: message.message_type || 'mqtt',
                summary: `MQTT消息 [${message.direction}]: ${message.topic}`,
                details: message.payload ? JSON.parse(message.payload) : {},
                level: message.status === 'success' ? 'success' : 'error',
            });
        };

        // 启动轮询
        const startPolling = () => {
            stopPolling();
            pollingTimer.value = setInterval(async () => {
                if (props.selectedBox) {
                    await fetchLogsFromApi();
                }
            }, POLL_INTERVAL);
        };

        // 停止轮询
        const stopPolling = () => {
            if (pollingTimer.value) {
                clearInterval(pollingTimer.value);
                pollingTimer.value = null;
            }
        };

        // 添加结果日志
        const addResultLog = (result) => {
            addLog({
                type: 'result',
                summary: `命令执行${result.success ? '成功' : '失败'}`,
                details: result,
                level: result.success ? 'success' : 'error',
            });
        };

        // 获取日志样式类
        const getLogClass = (log) => {
            const baseClass = 'log-entry';
            const typeClass = `log-${log.type}`;
            const levelClass = `log-${log.level || 'info'}`;
            return `${baseClass} ${typeClass} ${levelClass}`;
        };

        // 格式化时间
        const formatTime = (timestamp) => {
            if (!timestamp) return '';
            return formatToBJ(timestamp, 'HH:mm:ss');
        };

        // 清空日志
        const clearLogs = () => {
            logs.value = [];
            notify('success', '日志已清空');
        };

        // 导出日志
        const exportLogs = () => {
            const logText = logs.value.map(log => {
                const timeStr = formatTime(log.timestamp);
                const typeStr = log.type.toUpperCase();
                const detailsStr = log.details ? JSON.stringify(log.details, null, 2) : '';
                return `[${timeStr}] [${typeStr}] ${log.summary}\n${detailsStr}`;
            }).join('\n\n');

            const blob = new Blob([logText], {type: 'text/plain'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `mqtt-logs-${formatToBJ(nowUtcISOString(), 'YYYY-MM-DD')}.txt`;
            a.click();
            URL.revokeObjectURL(url);

            notify('success', '日志已导出');
        };

        // 从API获取日志
        const fetchLogsFromApi = async () => {
            if (!props.selectedBox) return;

            loadingLogs.value = true;
            try {
                const params = {};
                if (lastFetchTime) {
                    params.since = lastFetchTime;
                }

                const response = await axios.get(`/api/mqtt/logs/${props.selectedBox.id}`, {params});

                // 成功响应格式: {"data": {"logs": [...]}}
                const apiLogs = response.data.data.logs.map(log => ({
                    type: log.message_type,
                    summary: `${log.direction.toUpperCase()}: ${log.topic}`,
                    details: log.payload ? JSON.parse(log.payload) : {},
                    level: log.status === 'success' ? 'success' : 'error',
                    timestamp: log.processed_at,
                }));

                logs.value = [...apiLogs, ...logs.value];

                if (logs.value.length > 1000) {
                    logs.value = logs.value.slice(0, 1000);
                }

                lastFetchTime = nowUtcISOString();
            } catch (error) {
                console.error('Failed to fetch logs from API:', error);
                notify('error', '获取日志失败: ' + (error.response?.data?.message || error.message));
            } finally {
                loadingLogs.value = false;
            }
        };

        // 监听盒子选择变化
        watch(() => props.selectedBox, (newBox, oldBox) => {
            stopPolling();

            if (newBox) {
                lastFetchTime = null;
                fetchLogsFromApi();
                startPolling();
            }
        });

        // 组件挂载
        onMounted(() => {
            if (props.selectedBox) {
                fetchLogsFromApi();
                startPolling();
            }
        });

        // 组件卸载
        onUnmounted(() => {
            stopPolling();
        });

        // 暴露方法给父组件
        const exposeMethods = {
            addCommandLog,
            addMqttMessageLog,
            addResultLog,
            fetchLogsFromApi,
        };

        return {
            logs,
            logFilter,
            autoScroll,
            logScrollbar,
            loadingLogs,
            filteredLogs,
            addLog,
            addCommandLog,
            addMqttMessageLog,
            addResultLog,
            fetchLogsFromApi,
            getLogClass,
            formatTime,
            clearLogs,
            exportLogs,
            ...exposeMethods,
        };
    },
};
</script>

<style scoped>
.realtime-logs {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.logs-header {
  padding: 16px;
  border-bottom: 1px solid #dcdfe6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logs-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.logs-controls {
  display: flex;
  gap: 8px;
}

.logs-content {
  flex: 1;
  padding: 8px;
}

.log-container {
  display: flex;
  flex-direction: column-reverse;
}

.log-entry {
  display: flex;
  margin-bottom: 8px;
  padding: 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
}

.log-entry.log-info {
  background-color: #f0f9ff;
  border-left: 3px solid #409eff;
}

.log-entry.log-success {
  background-color: #f0f9ff;
  border-left: 3px solid #67c23a;
}

.log-entry.log-error {
  background-color: #fef0f0;
  border-left: 3px solid #f56c6c;
}

.log-entry.log-debug {
  background-color: #f4f4f5;
  border-left: 3px solid #909399;
}

.log-entry.log-measure {
  background-color: #f0f9ff;
}

.log-entry.log-query {
  background-color: #f0f5ff;
}

.log-entry.log-config {
  background-color: #fdf6ec;
}

.log-time {
  width: 80px;
  color: #909399;
  flex-shrink: 0;
}

.log-type {
  width: 60px;
  color: #409eff;
  font-weight: bold;
  flex-shrink: 0;
}

.log-message {
  flex: 1;
  margin-left: 8px;
}

.log-summary {
  color: #303133;
  margin-bottom: 4px;
}

.log-details {
  margin-top: 4px;
}

.log-details pre {
  background-color: #f5f7fa;
  padding: 8px;
  border-radius: 4px;
  font-size: 11px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.no-logs {
  padding: 20px;
  text-align: center;
}

.logs-footer {
  padding: 12px 16px;
  border-top: 1px solid #dcdfe6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.log-stats {
  font-size: 12px;
  color: #909399;
}

.auto-scroll {
  font-size: 12px;
}
</style>
